import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { User, Building2, Briefcase, RefreshCw, CheckCircle, ArrowLeft } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import type { OAuthProvider } from '../services/auth';
import MainTabsNav from '../components/MainTabsNav';

// provider = 교육 기관 (수업/인증서), employer = 기업 회원 (구인)
type MemberType = 'select' | 'user' | 'provider' | 'employer';

// 간편 로그인(소셜 로그인) — Google/Kakao는 Supabase Auth 세션 브릿지 구현 완료로 재활성화 (2026-08-26)
const SHOW_SOCIAL_LOGIN = true;
// 네이버는 커스텀 백엔드 OAuth 플로우는 구현돼 있으나 이번 재활성화 범위 밖이라 계속 비활성화
const SHOW_NAVER_LOGIN = false;

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signIn, loading, error, checkSession } = useAuthStore();
  const initialType = searchParams.get('type');
  const [memberType, setMemberType] = useState<MemberType>(
    initialType === 'user' || initialType === 'provider' || initialType === 'employer'
      ? initialType
      : 'select'
  );
  const [loginError, setLoginError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  // 이메일 미인증 상태 관리
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [resendError, setResendError] = useState('');

  const handleOAuthLogin = async (provider: OAuthProvider) => {
    try {
      setLoginError(null);
      await signIn(provider);
    } catch (err) {
      setLoginError('로그인 중 오류가 발생했습니다. 다시 시도해주세요.');
    }
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);

    if (!email || !password) {
      setLoginError('이메일과 비밀번호를 입력해주세요.');
      return;
    }

    setEmailLoading(true);
    try {
      const response = await fetch('/api/v1/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password, memberType }),
      });

      const data = await response.json();

      if (response.ok) {
        // 세션 체크로 상태 업데이트
        console.log('Login success, checking session...');
        await checkSession();
        console.log('Session check complete, navigating to home...');
        navigate('/');
      } else {
        // 이메일 미인증 에러 처리
        if (data.code === 'EMAIL_NOT_VERIFIED') {
          setEmailNotVerified(true);
          setUnverifiedEmail(data.email || email);
          setLoginError(data.error);
        } else {
          setEmailNotVerified(false);
          setLoginError(data.error || '로그인에 실패했습니다.');
        }
      }
    } catch (err) {
      setLoginError('로그인 중 오류가 발생했습니다.');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleBack = () => {
    setMemberType('select');
    setLoginError(null);
    setEmail('');
    setPassword('');
    setEmailNotVerified(false);
    setResendSuccess(false);
    setResendMessage('');
    setResendError('');
  };

  // 인증 이메일 재발송
  const handleResendVerification = async () => {
    if (!unverifiedEmail) return;

    setIsResending(true);
    setResendSuccess(false);
    setResendMessage('');
    setResendError('');

    try {
      const response = await fetch('/api/v1/auth/resend-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: unverifiedEmail }),
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setResendSuccess(true);
        setResendMessage(data.message || '인증 이메일이 재발송되었습니다.');
      } else {
        setResendError(data.error || '인증 이메일 재발송에 실패했습니다.');
      }
    } catch (err) {
      console.error('Resend verification error:', err);
      setResendError('인증 이메일 재발송에 실패했습니다.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <>
      <MainTabsNav />
      <div className="flex items-center justify-center py-8 px-4">
        <div className="w-full max-w-md">
          {/* Back button for member type */}
          {memberType !== 'select' && (
            <button
              onClick={handleBack}
              className="flex items-center gap-2 text-gray-600 hover:text-primary transition-colors mb-4"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>회원 유형 선택</span>
            </button>
          )}

          {/* Logo */}
          <div className="text-center mb-8">
            <Link to="/">
              <img
                src="/logo/stampfit-logo.png"
                alt="StampFit"
                className="w-[180px] mx-auto"
              />
            </Link>
            <p className="text-gray-500 mt-2">피트니스 자격 인증 플랫폼</p>
          </div>

          {/* Member Type Selection */}
          {memberType === 'select' && (
            <div className="bg-white rounded-2xl shadow-soft p-8">
              <h1 className="text-2xl font-bold text-gray-900 text-center mb-2">
                로그인
              </h1>
              <p className="text-gray-500 text-center mb-8">
                회원 유형을 선택해주세요
              </p>

              <div className="space-y-4">
                {/* 일반 회원 */}
                <button
                  onClick={() => setMemberType('user')}
                  className="w-full flex items-center gap-4 p-6 border-2 border-gray-200 rounded-xl hover:border-primary hover:bg-primary/5 transition-all group"
                >
                  <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <User className="w-7 h-7 text-primary" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-bold text-gray-900 text-lg">일반 회원</h3>
                    <p className="text-gray-500 text-sm">피트니스 강사, 트레이너</p>
                  </div>
                </button>

                {/* 교육 기관 */}
                <button
                  onClick={() => setMemberType('provider')}
                  className="w-full flex items-center gap-4 p-6 border-2 border-gray-200 rounded-xl hover:border-accent hover:bg-accent/5 transition-all group"
                >
                  <div className="w-14 h-14 bg-accent/10 rounded-full flex items-center justify-center group-hover:bg-accent/20 transition-colors">
                    <Building2 className="w-7 h-7 text-accent" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-bold text-gray-900 text-lg">교육 기관</h3>
                    <p className="text-gray-500 text-sm">교육 업체, 자격 발급 기관</p>
                  </div>
                </button>

                {/* 기업 회원 (구인) */}
                <button
                  onClick={() => setMemberType('employer')}
                  className="w-full flex items-center gap-4 p-6 border-2 border-gray-200 rounded-xl hover:border-emerald-500 hover:bg-emerald-50 transition-all group"
                >
                  <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                    <Briefcase className="w-7 h-7 text-emerald-600" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-bold text-gray-900 text-lg">기업 회원</h3>
                    <p className="text-gray-500 text-sm">채용 공고, 이력서 열람</p>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* 일반 회원 로그인 */}
          {memberType === 'user' && (
            <div className="bg-white rounded-2xl shadow-soft p-8">
              <h1 className="text-2xl font-bold text-gray-900 text-center mb-6">
                일반 회원 로그인
              </h1>

              {/* Error Message */}
              {(loginError || error) && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600 text-sm text-center mb-2">
                    {loginError || error?.message || '로그인 중 오류가 발생했습니다.'}
                  </p>
                  {/* 이메일 미인증 시 재발송 버튼 */}
                  {emailNotVerified && (
                    <div className="mt-3 pt-3 border-t border-red-200">
                      {resendSuccess ? (
                        <div className="flex items-center justify-center gap-2 text-green-600 text-sm">
                          <CheckCircle className="w-4 h-4" />
                          <span>{resendMessage}</span>
                        </div>
                      ) : (
                        <button
                          onClick={handleResendVerification}
                          disabled={isResending}
                          className="w-full flex items-center justify-center gap-2 text-sm text-primary hover:text-primary/80 disabled:opacity-50"
                        >
                          <RefreshCw className={`w-4 h-4 ${isResending ? 'animate-spin' : ''}`} />
                          {isResending ? '발송 중...' : '인증 이메일 재발송'}
                        </button>
                      )}
                      {resendError && (
                        <p className="mt-2 text-xs text-red-600 text-center">{resendError}</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {SHOW_SOCIAL_LOGIN && (
                <>
                  {/* Social Login */}
                  <div className="space-y-3">
                    <p className="text-center text-gray-500 text-sm mb-4">간편 로그인</p>

                    {/* Naver */}
                    {SHOW_NAVER_LOGIN && (
                      <button
                        onClick={() => handleOAuthLogin('naver')}
                        disabled={loading}
                        className="w-full flex items-center justify-center gap-3 bg-[#03C75A] hover:bg-[#02B350] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold px-6 py-3 rounded-lg transition-colors"
                      >
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M16.273 12.845L7.376 0H0v24h7.727V11.155L16.624 24H24V0h-7.727v12.845z"/>
                        </svg>
                        네이버로 시작하기
                      </button>
                    )}

                    {/* Google */}
                    <button
                      onClick={() => handleOAuthLogin('google')}
                      disabled={loading}
                      className="w-full flex items-center justify-center gap-3 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 font-semibold px-6 py-3 rounded-lg border border-gray-300 transition-colors"
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      Google로 시작하기
                    </button>

                    {/* Kakao */}
                    <button
                      onClick={() => handleOAuthLogin('kakao')}
                      disabled={loading}
                      className="w-full flex items-center justify-center gap-3 bg-[#FEE500] hover:bg-[#FDD800] disabled:opacity-50 disabled:cursor-not-allowed text-[#000000]/85 font-semibold px-6 py-3 rounded-lg transition-colors"
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 3c5.799 0 10.5 3.664 10.5 8.185 0 4.52-4.701 8.184-10.5 8.184a13.5 13.5 0 01-1.727-.11l-4.408 2.883c-.501.265-.678.236-.472-.413l.892-3.678c-2.88-1.46-4.785-3.99-4.785-6.866C1.5 6.665 6.201 3 12 3z"/>
                      </svg>
                      카카오로 시작하기
                    </button>
                  </div>

                  {/* Divider */}
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200" />
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-4 bg-white text-gray-500">또는</span>
                    </div>
                  </div>
                </>
              )}

              {/* Email Login Form */}
              <form className="space-y-4" onSubmit={handleEmailLogin}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    이메일
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="email@example.com"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent/50 focus:border-accent outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    비밀번호
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent/50 focus:border-accent outline-none transition-all"
                  />
                </div>
                <button
                  type="submit"
                  disabled={emailLoading}
                  className={`w-full font-semibold px-6 py-3 rounded-lg transition-colors ${
                    emailLoading
                      ? 'bg-gray-400 cursor-not-allowed text-white'
                      : 'bg-primary hover:bg-primary-600 text-white'
                  }`}
                >
                  {emailLoading ? '로그인 중...' : '이메일로 로그인'}
                </button>
              </form>

              {/* 아이디/비밀번호 찾기 */}
              <div className="mt-4 flex items-center justify-center gap-3 text-sm text-gray-500">
                <Link to="/find-id" className="hover:text-primary hover:underline">
                  아이디 찾기
                </Link>
                <span className="text-gray-300">|</span>
                <Link to="/forgot-password" className="hover:text-primary hover:underline">
                  비밀번호 찾기
                </Link>
              </div>

              {/* Links */}
              <div className="mt-6 text-center text-sm">
                <span className="text-gray-500">계정이 없으신가요?</span>{' '}
                <Link to="/signup?type=user" className="text-primary hover:underline font-medium">
                  회원가입
                </Link>
              </div>
            </div>
          )}

          {/* 교육 기관 / 기업 회원 로그인 (UI 라벨·색상 분기) */}
          {(memberType === 'provider' || memberType === 'employer') && (
            <div className="bg-white rounded-2xl shadow-soft p-8">
              <div className="flex items-center justify-center gap-2 mb-6">
                {memberType === 'provider' ? (
                  <Building2 className="w-6 h-6 text-accent" />
                ) : (
                  <Briefcase className="w-6 h-6 text-emerald-600" />
                )}
                <h1 className="text-2xl font-bold text-gray-900">
                  {memberType === 'provider' ? '교육 기관 로그인' : '기업 회원 로그인'}
                </h1>
              </div>

              {/* Error Message */}
              {(loginError || error) && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600 text-sm text-center mb-2">
                    {loginError || error?.message || '로그인 중 오류가 발생했습니다.'}
                  </p>
                  {/* 이메일 미인증 시 재발송 버튼 */}
                  {emailNotVerified && (
                    <div className="mt-3 pt-3 border-t border-red-200">
                      {resendSuccess ? (
                        <div className="flex items-center justify-center gap-2 text-green-600 text-sm">
                          <CheckCircle className="w-4 h-4" />
                          <span>{resendMessage}</span>
                        </div>
                      ) : (
                        <button
                          onClick={handleResendVerification}
                          disabled={isResending}
                          className={`w-full flex items-center justify-center gap-2 text-sm disabled:opacity-50 ${
                            memberType === 'employer'
                              ? 'text-emerald-600 hover:text-emerald-700'
                              : 'text-accent hover:text-accent/80'
                          }`}
                        >
                          <RefreshCw className={`w-4 h-4 ${isResending ? 'animate-spin' : ''}`} />
                          {isResending ? '발송 중...' : '인증 이메일 재발송'}
                        </button>
                      )}
                      {resendError && (
                        <p className="mt-2 text-xs text-red-600 text-center">{resendError}</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Email Login Form Only */}
              <form className="space-y-4" onSubmit={handleEmailLogin}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    이메일
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="company@example.com"
                    className={`w-full px-4 py-3 border border-gray-300 rounded-lg outline-none transition-all ${
                      memberType === 'employer'
                        ? 'focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500'
                        : 'focus:ring-2 focus:ring-accent/50 focus:border-accent'
                    }`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    비밀번호
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className={`w-full px-4 py-3 border border-gray-300 rounded-lg outline-none transition-all ${
                      memberType === 'employer'
                        ? 'focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500'
                        : 'focus:ring-2 focus:ring-accent/50 focus:border-accent'
                    }`}
                  />
                </div>
                <button
                  type="submit"
                  disabled={emailLoading}
                  className={`w-full font-semibold px-6 py-3 rounded-lg transition-colors ${
                    emailLoading
                      ? 'bg-gray-400 cursor-not-allowed text-white'
                      : memberType === 'employer'
                      ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                      : 'bg-accent hover:bg-accent/90 text-white'
                  }`}
                >
                  {emailLoading ? '로그인 중...' : '로그인'}
                </button>
              </form>

              {/* 아이디/비밀번호 찾기 */}
              <div className="mt-4 flex items-center justify-center gap-3 text-sm text-gray-500">
                <Link to="/find-id" className="hover:underline hover:text-gray-700">
                  아이디 찾기
                </Link>
                <span className="text-gray-300">|</span>
                <Link to="/forgot-password" className="hover:underline hover:text-gray-700">
                  비밀번호 찾기
                </Link>
              </div>

              {/* Links */}
              <div className="mt-6 text-center text-sm">
                <span className="text-gray-500">계정이 없으신가요?</span>{' '}
                <Link
                  to={`/signup?type=${memberType}`}
                  className={`hover:underline font-medium ${
                    memberType === 'employer' ? 'text-emerald-600' : 'text-accent'
                  }`}
                >
                  {memberType === 'provider' ? '교육 기관 회원가입' : '기업 회원가입'}
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
